/**
 * Sensor Edit Module
 * Handles CRUD operations for sensors (Create, Update, Delete)
 * and alarm template assignment functionality
 */

import { t } from '../core/i18n.js';
import { auth } from '../core/auth.js';
import { apiCall } from '../core/api.js';
import { API_BASE_URL } from '../core/config.js';
import { showError, showSuccess } from '../core/ui.js';
import { sensorsInfiniteScroll, loadSensorsData } from './sensors.js';
import { loadAccountsForSensorModal } from './sensors-modals.js';

/**
 * Close modal helper
 */
function closeModal() {
    const modalContainer = document.getElementById('modal');
    if (modalContainer) {
        modalContainer.innerHTML = '';
        modalContainer.classList.add('hidden');
    }
}

/**
 * Get current user from localStorage
 */
function getCurrentUser() {
    const userStr = localStorage.getItem('current_user');
    const user = userStr ? JSON.parse(userStr) : null;
    
    // Initialize permissions if user exists and permissions not yet initialized
    if (user && (!window.permissions.checker || window.permissions.checker.userRole !== user.role)) {
        window.permissions.init(user);
    }
    
    return user;
}

/**
 * Format weekdays object for display
 */
function formatWeekdays(weekdays) {
    const days = [];
    const dayNames = {
        monday: t('weekdays.monday'),
        tuesday: t('weekdays.tuesday'),
        wednesday: t('weekdays.wednesday'),
        thursday: t('weekdays.thursday'),
        friday: t('weekdays.friday'),
        saturday: t('weekdays.saturday'),
        sunday: t('weekdays.sunday')
    };
    
    for (const [day, enabled] of Object.entries(weekdays)) {
        if (enabled) {
            days.push(dayNames[day]);
        }
    }
    
    return days.length > 0 ? days.join(', ') : t('common.none');
}

/**
 * Close nested-nested modal (used for alarm assignment on top of sensor edit)
 */
export function closeNestedNestedModal() {
    const nestedNestedModal = document.getElementById('nested-nested-modal');
    if (nestedNestedModal) {
        nestedNestedModal.classList.add('hidden');
        nestedNestedModal.innerHTML = '';
    }
}

/**
 * Show sensor edit modal
 */
export async function showEditSensorModal(sensorId, sourceLocationId = null) {
    try {
        // Store source location ID if we came from location edit
        if (sourceLocationId) {
            window.editSensorSourceLocation = sourceLocationId;
        }
        
        // Fetch sensor data
        console.log('Fetching sensor with ID:', sensorId);
        const response = await fetch(`${API_BASE_URL}/sensors/${sensorId}`, {
            headers: { 'Authorization': `Bearer ${auth.token}` }
        });

        if (!response.ok) {
            throw new Error('Failed to fetch sensor data');
        }

        const sensor = await response.json();
        console.log('Received sensor data from API:', JSON.stringify(sensor, null, 2));
        console.log('Sensor location_id field:', sensor.location_id);
        console.log('Sensor location_name field:', sensor.location_name);
        console.log('All sensor fields:', Object.keys(sensor));
        
        // Check if current user has delete permissions (SuperAdmin and Administrator)
        const currentUser = getCurrentUser();
        const canDelete = currentUser && (currentUser.role === 'SuperAdmin' || currentUser.role === 'Administrator');

        // Check if main modal is already open (e.g., location edit modal)
        const mainModal = document.getElementById('modal');
        const isNestedModal = mainModal && !mainModal.classList.contains('hidden');
        const zIndex = isNestedModal ? 'z-[55]' : 'z-50';

        const modalHtml = `
            <div class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center ${zIndex}">
                <div class="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                    <div class="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
                        <h3 class="text-lg font-medium text-gray-900">Edit Sensor</h3>
                    </div>
                    <form onsubmit="handleEditSensor(event)" class="px-6 py-4 space-y-4">
                        <input type="hidden" id="edit-sensor-id" value="${sensor.id}">
                        <input type="hidden" id="edit-sensor-configuration" value='${JSON.stringify(sensor.configuration || {}).replace(/'/g, "&#39;")}'>
                        
                        <!-- Row 1: Sensor Name and Sensor ID -->
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">
                                    Sensor Name <span class="text-red-500">*</span>
                                </label>
                                <input type="text" 
                                       id="edit-sensor-name" 
                                       required
                                       value="${sensor.name || ''}"
                                       class="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                                       placeholder="Enter sensor name">
                            </div>

                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">
                                    Sensor ID <span class="text-red-500">*</span>
                                </label>
                                <input type="text" 
                                       id="edit-sensor-id-field" 
                                       required
                                       value="${sensor.sensor_id || ''}"
                                       class="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                                       placeholder="Enter unique sensor ID">
                                <p class="mt-1 text-sm text-gray-500">Unique identifier for the sensor</p>
                            </div>
                        </div>

                        <!-- Row 2: Sensor Type and Status -->
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">
                                    Sensor Type <span class="text-red-500">*</span>
                                </label>
                                <select id="edit-sensor-type" 
                                        required
                                        onchange="window.onEditSensorTypeChange(this.value)"
                                        class="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500">
                                    <option value="">Select sensor type</option>
                                    <option value="temperature" ${sensor.sensor_type === 'temperature' ? 'selected' : ''}>Temperature</option>
                                    <option value="temperature_humidity" ${sensor.sensor_type === 'temperature_humidity' ? 'selected' : ''}>Temperature/Humidity/Pressure</option>
                                    <option value="temp_humidity" ${sensor.sensor_type === 'temp_humidity' ? 'selected' : ''}>Temperature/Humidity</option>
                                    <option value="tracker" ${sensor.sensor_type === 'tracker' ? 'selected' : ''}>Tracker</option>
                                    <option value="gateway" ${sensor.sensor_type === 'gateway' ? 'selected' : ''}>Gateway</option>
                                    <option value="card_reader" ${sensor.sensor_type === 'card_reader' ? 'selected' : ''}>Card reader</option>
                                    <option value="activity" ${sensor.sensor_type === 'activity' ? 'selected' : ''}>Activity</option>
                                    <option value="light" ${sensor.sensor_type === 'light' ? 'selected' : ''}>Light</option>
                                </select>
                            </div>

                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">
                                    Status
                                </label>
                                <select id="edit-sensor-status" 
                                        class="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500">
                                    <option value="active" ${sensor.status === 'active' ? 'selected' : ''}>Active</option>
                                    <option value="inactive" ${sensor.status === 'inactive' ? 'selected' : ''}>Inactive</option>
                                    <option value="maintenance" ${sensor.status === 'maintenance' ? 'selected' : ''}>Maintenance</option>
                                </select>
                            </div>
                        </div>

                        <!-- BLE MAC Address (shown only for BLE temp/humidity sensors) -->
                        <div id="edit-sensor-mac-wrapper" class="${sensor.sensor_type === 'temp_humidity' ? '' : 'hidden'}">
                            <label class="block text-sm font-medium text-gray-700 mb-1">
                                ${t('sensors.macAddress')}
                            </label>
                            <input type="text"
                                   id="edit-sensor-mac"
                                   value="${sensor.configuration?.mac_address || ''}"
                                   class="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500 font-mono"
                                   placeholder="e.g., D0:83:D4:01:72:74">
                            <p class="mt-1 text-sm text-gray-500">${t('sensors.macAddressHint')}</p>
                        </div>

                        <!-- Account (owning account) -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">
                                Account
                            </label>
                            <p class="text-xs text-gray-500 mb-2">
                                The account that owns this sensor. Move a newly auto-created sensor to the end customer's account here. Current: <span class="font-medium">${sensor.account_name || 'N/A'}</span>
                            </p>
                            <div id="edit-sensor-account-tree-container" class="border border-gray-300 rounded-md p-3 max-h-56 overflow-y-auto bg-white">
                                <div class="text-gray-400 text-sm">Loading accounts...</div>
                            </div>
                            <input type="hidden" id="edit-sensor-account" value="${sensor.account_id || ''}">
                        </div>

                        <!-- Row 3: Associated Location and Asset -->
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">
                                    Associated Location
                                </label>
                                
                                <!-- Current location (if exists) -->
                                <div id="current-location-item" class="mb-2 hidden">
                                    <label class="flex items-center p-3 border border-blue-300 bg-blue-50 rounded-md cursor-pointer hover:bg-blue-100">
                                        <input type="radio" 
                                               name="sensor-location-radio" 
                                               value="" 
                                               id="radio-current-location"
                                               checked
                                               class="mr-3 h-4 w-4 text-primary-600 focus:ring-primary-500">
                                        <div class="flex-1">
                                            <div class="font-medium text-gray-900" id="current-location-name"></div>
                                            <div class="text-xs text-gray-500">Current location</div>
                                        </div>
                                    </label>
                                </div>
                                
                                <!-- Search for locations -->
                                <div>
                                    <input type="text" 
                                           id="location-search" 
                                           placeholder="Search locations..."
                                           class="w-full border border-gray-300 rounded-md px-3 py-2 mb-2 focus:ring-primary-500 focus:border-primary-500">
                                    <div id="location-results" 
                                         class="border border-gray-300 rounded-md max-h-48 overflow-y-auto mb-2 hidden">
                                        <!-- Search results will appear here -->
                                    </div>
                                </div>
                                
                                <!-- Unassociate option -->
                                <label class="flex items-center p-3 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50">
                                    <input type="radio" 
                                           name="sensor-location-radio" 
                                           value="" 
                                           id="radio-no-location"
                                           class="mr-3 h-4 w-4 text-primary-600 focus:ring-primary-500">
                                    <div class="flex-1">
                                        <div class="font-medium text-gray-900">None</div>
                                        <div class="text-xs text-gray-500">Unassociated (belongs to main account)</div>
                                    </div>
                                </label>
                                
                                <input type="hidden" id="edit-sensor-location" value="">
                                <p class="text-xs text-gray-500 mt-2">Associate this sensor with a location</p>
                            </div>

                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">
                                    Associated Asset
                                </label>
                                <select id="edit-sensor-asset" 
                                        onchange="handleLocationAssetChange('asset')"
                                        class="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500">
                                    <option value="">None - Unassociated</option>
                                </select>
                                <p class="text-xs text-gray-500 mt-1">Select an asset to associate this sensor</p>
                            </div>
                        </div>

                        <!-- Alarms -->
                        <div class="border-t border-gray-200 pt-4">
                            <div class="flex justify-between items-center mb-3">
                                <label class="block text-sm font-medium text-gray-700">
                                    Alarms
                                </label>
                                <button type="button" 
                                        onclick="showAssignAlarmModal('${sensor.id}', '${sensor.sensor_type}')"
                                        class="px-3 py-1 text-xs font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700">
                                    + Assign Alarm
                                </button>
                            </div>
                            <div id="sensor-alarm-assignments-${sensor.id}" class="space-y-2">
                                <div class="text-sm text-gray-500">Loading alarm assignments...</div>
                            </div>
                        </div>

                        <!-- Actions -->
                        <div class="flex justify-between items-center pt-4 border-t border-gray-200">
                            ${canDelete ? `
                                <button type="button" 
                                        onclick="confirmDeleteSensor('${sensor.id}')"
                                        class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
                                    Delete Sensor
                                </button>
                            ` : '<div></div>'}
                            <div class="flex space-x-3">
                                <button type="button" 
                                        onclick="closeEditSensorModal()"
                                        class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                                    Cancel
                                </button>
                                <button type="submit"
                                        class="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700">
                                    Update Sensor
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        `;

        // Determine which modal container to use
        let modalContainer;
        if (isNestedModal) {
            // Use nested modal container (sensor edit on top of location edit)
            modalContainer = document.getElementById('nested-modal');
            if (!modalContainer) {
                modalContainer = document.createElement('div');
                modalContainer.id = 'nested-modal';
                modalContainer.className = 'hidden';
                document.body.appendChild(modalContainer);
            }
        } else {
            // Use main modal container
            modalContainer = document.getElementById('modal');
        }
        
        modalContainer.innerHTML = modalHtml;
        modalContainer.classList.remove('hidden');

        // Load locations and assets for the sensor's account
        await loadLocationsAndAssetsForSensor(sensor);

        // Load account tree so the sensor can be moved to another account
        await loadAccountsForSensorModal('edit-sensor-account-tree-container', 'edit-sensor-account', sensor.account_id);

        // Load alarm assignments for this sensor
        await loadSensorAlarmAssignments(sensor.id);

    } catch (error) {
        console.error('Error loading sensor for edit:', error);
        showError('Failed to load sensor data');
    }
}

/**
 * Helper function to handle location/asset selection mutual exclusivity
 */
export function handleLocationAssetChange(changedType) {
    if (changedType === 'location') {
        const locationSelect = document.getElementById('edit-sensor-location');
        const assetSelect = document.getElementById('edit-sensor-asset');
        if (locationSelect.value) {
            assetSelect.value = '';
            assetSelect.disabled = true;
        } else {
            assetSelect.disabled = false;
        }
    } else if (changedType === 'asset') {
        const locationSelect = document.getElementById('edit-sensor-location');
        const assetSelect = document.getElementById('edit-sensor-asset');
        if (assetSelect.value) {
            locationSelect.value = '';
            locationSelect.disabled = true;
        } else {
            locationSelect.disabled = false;
        }
    }
}

/**
 * Load locations and assets for sensor editing
 */
async function loadLocationsAndAssetsForSensor(sensor) {
    try {
        const token = auth.token;
        
        console.log('Loading locations/assets for sensor:', sensor);
        console.log('Sensor location_id:', sensor.location_id);
        console.log('Sensor location_name:', sensor.location_name);
        console.log('Sensor asset_id:', sensor.asset_id);
        
        const locationInput = document.getElementById('edit-sensor-location');
        const currentLocationItem = document.getElementById('current-location-item');
        const currentLocationName = document.getElementById('current-location-name');
        const radioCurrentLocation = document.getElementById('radio-current-location');
        const radioNoLocation = document.getElementById('radio-no-location');
        
        // Show current location if it exists
        if (sensor.location_id && sensor.location_name) {
            currentLocationName.textContent = sensor.location_name;
            currentLocationItem.classList.remove('hidden');
            radioCurrentLocation.value = sensor.location_id;
            radioCurrentLocation.checked = true;
            locationInput.value = sensor.location_id;
            
            // Disable asset select if location is set
            document.getElementById('edit-sensor-asset').disabled = true;
        } else {
            // No current location - select "None" option
            radioNoLocation.checked = true;
            locationInput.value = '';
        }
        
        // Setup location search with debouncing
        let searchTimeout;
        const locationSearchInput = document.getElementById('location-search');
        const locationResults = document.getElementById('location-results');
        
        locationSearchInput.addEventListener('input', (e) => {
            clearTimeout(searchTimeout);
            const query = e.target.value.trim();
            
            if (query.length < 2) {
                locationResults.classList.add('hidden');
                locationResults.innerHTML = '';
                return;
            }
            
            searchTimeout = setTimeout(async () => {
                await searchLocationsForSensor(query);
            }, 300); // 300ms debounce
        });
        
        // Setup radio button change handlers
        document.querySelectorAll('input[name="sensor-location-radio"]').forEach(radio => {
            radio.addEventListener('change', (e) => {
                locationInput.value = e.target.value;
                
                // Enable/disable asset selector based on location selection
                const hasLocation = e.target.value !== '';
                document.getElementById('edit-sensor-asset').disabled = hasLocation;
                if (hasLocation) {
                    document.getElementById('edit-sensor-asset').value = '';
                }
            });
        });
        
        // Fetch all accessible assets
        const assetsResponse = await fetch(`${API_BASE_URL}/assets`, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        
        if (assetsResponse.ok) {
            const assetsData = await assetsResponse.json();
            const assets = Array.isArray(assetsData) ? assetsData : (assetsData.assets || assetsData.items || []);
            const assetSelect = document.getElementById('edit-sensor-asset');
            
            console.log('Available assets:', assets);
            
            assets.forEach(asset => {
                const option = document.createElement('option');
                option.value = asset.id;
                option.textContent = asset.name;
                if (sensor.asset_id && sensor.asset_id === asset.id) {
                    option.selected = true;
                    console.log('Selected asset:', asset.name);
                }
                assetSelect.appendChild(option);
            });
        } else if (assetsResponse.status === 404) {
            console.log('Assets endpoint not available yet');
            const assetSelect = document.getElementById('edit-sensor-asset');
            assetSelect.disabled = true;
            const option = document.createElement('option');
            option.value = '';
            option.textContent = 'Assets not available yet';
            assetSelect.appendChild(option);
        }
        
        // Set initial state based on current association
        if (sensor.asset_id) {
            // Disable location options if asset is selected
            document.querySelectorAll('input[name="sensor-location-radio"]').forEach(radio => {
                radio.disabled = true;
            });
            locationSearchInput.disabled = true;
            console.log('Location options disabled because asset is set');
        }
        
    } catch (error) {
        console.error('Error loading locations/assets:', error);
    }
}

/**
 * Search locations with pagination
 */
async function searchLocationsForSensor(query) {
    const locationResults = document.getElementById('location-results');
    locationResults.classList.remove('hidden');
    locationResults.innerHTML = '<div class="p-3 text-sm text-gray-500">Searching...</div>';
    
    try {
        const token = auth.token;
        const searchUrl = `${API_BASE_URL}/locations?search=${encodeURIComponent(query)}&limit=100`;
        console.log('Searching locations with URL:', searchUrl);
        
        const response = await fetch(searchUrl, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        
        if (!response.ok) {
            throw new Error('Failed to search locations');
        }
        
        const data = await response.json();
        console.log('Search results:', data);
        const locations = Array.isArray(data) ? data : (data.locations || data.items || []);
        console.log('Locations array:', locations.length, 'items');
        
        if (locations.length === 0) {
            locationResults.innerHTML = '<div class="p-3 text-sm text-gray-500">No locations found</div>';
            return;
        }
        
        // Show count if there might be more results
        const countText = locations.length >= 100 ? ` (showing first 100)` : ` (${locations.length} found)`;
        
        // Display results as radio buttons
        locationResults.innerHTML = `
            <div class="p-2 bg-gray-50 border-b border-gray-200 text-xs text-gray-600">
                ${locations.length} result${locations.length !== 1 ? 's' : ''}${countText}
            </div>
        ` + locations.map(location => `
            <label class="flex items-center p-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0">
                <input type="radio" 
                       name="sensor-location-radio" 
                       value="${location.id}"
                       class="mr-3 h-4 w-4 text-primary-600 focus:ring-primary-500">
                <div class="flex-1">
                    <div class="text-sm font-medium text-gray-900">${location.name}</div>
                    ${location.address ? `<div class="text-xs text-gray-500">${location.address}</div>` : ''}
                </div>
            </label>
        `).join('');
        
        // Add event listeners to new radio buttons
        locationResults.querySelectorAll('input[type="radio"]').forEach(radio => {
            radio.addEventListener('change', (e) => {
                const locationInput = document.getElementById('edit-sensor-location');
                locationInput.value = e.target.value;
                
                // Find the selected location data
                const selectedLocation = locations.find(loc => loc.id === e.target.value);
                
                // Update current location display to show the new selection
                if (selectedLocation) {
                    const currentLocationItem = document.getElementById('current-location-item');
                    const currentLocationName = document.getElementById('current-location-name');
                    const currentLocationRadio = document.getElementById('radio-current-location');
                    const noLocationRadio = document.getElementById('radio-no-location');
                    
                    // Update the current location display
                    currentLocationName.innerHTML = `
                        <div class="text-sm font-medium text-gray-900">${selectedLocation.name}</div>
                        ${selectedLocation.address ? `<div class="text-xs text-gray-500">${selectedLocation.address}</div>` : ''}
                    `;
                    
                    // Show the current location box and check its radio
                    currentLocationItem.classList.remove('hidden');
                    currentLocationRadio.checked = true;
                    currentLocationRadio.value = selectedLocation.id;
                    
                    // Uncheck the "None" option
                    noLocationRadio.checked = false;
                }
                
                // Disable asset selector when location is selected
                document.getElementById('edit-sensor-asset').disabled = true;
                document.getElementById('edit-sensor-asset').value = '';
                
                // Clear search and hide results
                document.getElementById('location-search').value = '';
                locationResults.classList.add('hidden');
            });
        });
        
    } catch (error) {
        console.error('Error searching locations:', error);
        locationResults.innerHTML = '<div class="p-3 text-sm text-red-500">Error searching locations</div>';
    }
}

/**
 * Handle sensor update form submission
 */
export async function handleEditSensor(event) {
    event.preventDefault();

    const sensorId = document.getElementById('edit-sensor-id').value;
    const name = document.getElementById('edit-sensor-name').value.trim();
    const sensorIdField = document.getElementById('edit-sensor-id-field').value.trim();
    const sensorType = document.getElementById('edit-sensor-type').value;
    const status = document.getElementById('edit-sensor-status').value;
    const locationId = document.getElementById('edit-sensor-location').value || null;
    const assetId = document.getElementById('edit-sensor-asset').value || null;
    const accountId = document.getElementById('edit-sensor-account')?.value || null;

    // Validate required fields
    if (!name || !sensorIdField || !sensorType) {
        showError('Please fill in all required fields');
        return;
    }

    // Determine association type based on what's selected
    let associationType = 'account';  // Default: unassociated, stays with main account
    let finalLocationId = null;
    let finalAssetId = null;
    
    if (locationId) {
        associationType = 'location';
        finalLocationId = locationId;
    } else if (assetId) {
        associationType = 'asset';
        finalAssetId = assetId;
    }

    try {
        const sensorData = {
            name,
            sensor_id: sensorIdField,
            sensor_type: sensorType,
            status,
            association_type: associationType,
            account_id: accountId,
            location_id: finalLocationId,
            asset_id: finalAssetId
        };

        // Preserve existing configuration and set/clear the BLE MAC (beacon mode)
        let configuration = {};
        try {
            configuration = JSON.parse(document.getElementById('edit-sensor-configuration')?.value || '{}');
        } catch (_) {
            configuration = {};
        }
        if (sensorType === 'temp_humidity') {
            const macAddress = document.getElementById('edit-sensor-mac')?.value.trim();
            if (macAddress) {
                configuration.mac_address = macAddress.toLowerCase();
            } else {
                delete configuration.mac_address;
            }
        }
        if (Object.keys(configuration).length > 0 || document.getElementById('edit-sensor-configuration')) {
            sensorData.configuration = configuration;
        }

        const response = await fetch(`${API_BASE_URL}/sensors/${sensorId}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${auth.token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(sensorData)
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to update sensor');
        }

        // Close modal and refresh sensor list to show updated alarm icons
        await closeEditSensorModal();

    } catch (error) {
        console.error('Error updating sensor:', error);
        showError(error.message || 'Failed to update sensor');
    }
}

/**
 * Show delete confirmation modal
 */
export function confirmDeleteSensor(sensorId) {
    const modal = document.getElementById('modal');
    modal.innerHTML = `
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
            <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
                <div class="px-6 py-4">
                    <div class="flex items-center">
                        <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                            <svg class="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                            </svg>
                        </div>
                    </div>
                    <div class="mt-3 text-center">
                        <h3 class="text-lg font-medium text-gray-900">Delete Sensor</h3>
                        <div class="mt-2">
                            <p class="text-sm text-gray-500">
                                Are you sure you want to delete this sensor? This action cannot be undone.
                            </p>
                        </div>
                    </div>
                </div>
                <div class="bg-gray-50 px-4 py-3 flex justify-end space-x-3">
                    <button type="button" 
                            onclick="closeModal()"
                            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                        Cancel
                    </button>
                    <button type="button" 
                            onclick="deleteSensor('${sensorId}')"
                            class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700">
                        Delete
                    </button>
                </div>
            </div>
        </div>
    `;
}

/**
 * Delete sensor
 */
export async function deleteSensor(sensorId) {
    try {
        const response = await fetch(`${API_BASE_URL}/sensors/${sensorId}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${auth.token}`
            }
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to delete sensor');
        }

        // Close modal
        closeModal();

        // Show success message
        showSuccess('Sensor deleted successfully');

        // Refresh the sensors list
        await loadSensorsData(true);

    } catch (error) {
        console.error('Error deleting sensor:', error);
        showError(error.message || 'Failed to delete sensor');
    }
}

/**
 * Close sensor edit modal
 */
export async function closeEditSensorModal() {
    // Check if sensor modal is in nested modal (opened from location edit)
    const nestedModal = document.getElementById('nested-modal');
    const isNested = nestedModal && !nestedModal.classList.contains('hidden');
    const sourceLocationId = window.editSensorSourceLocation;
    
    if (isNested && sourceLocationId) {
        // Close the nested modal (location modal stays open underneath)
        const closeNestedModal = window.closeNestedModal || (() => {
            if (nestedModal) {
                nestedModal.classList.add('hidden');
                nestedModal.innerHTML = '';
            }
        });
        closeNestedModal();
        
        // Clear the source location
        window.editSensorSourceLocation = null;
        
        // Reload sensors in the location edit modal to show updated alarm icons
        if (window.refreshLocationModalSensors) {
            // Use new unified modal refresh function
            await window.refreshLocationModalSensors();
        } else if (window.loadAvailableSensorsForLocation) {
            // Fallback for old modal
            await window.loadAvailableSensorsForLocation('edit-location-sensors-container', sourceLocationId);
        }
    } else {
        // Clear source location if set
        window.editSensorSourceLocation = null;
        
        // Close main modal
        closeModal();
        
        // Force refresh sensor list to show updated alarm icons
        console.log('Refreshing sensor list after closing edit modal...');
        try {
            // Reset scroll state and reload from beginning to ensure fresh data
            sensorsInfiniteScroll.currentPage = 1;
            sensorsInfiniteScroll.allSensors = [];
            sensorsInfiniteScroll.hasMore = true;
            await loadSensorsData(true);
            console.log('Sensor list refreshed successfully');
        } catch (error) {
            console.error('Error refreshing sensor list:', error);
        }
    }
}

// ===== Alarm Template Assignment Functions =====

/**
 * Load sensor alarm assignments
 */
async function loadSensorAlarmAssignments(sensorId) {
    try {
        console.log('Loading alarm assignments for sensor:', sensorId);
        console.log('Making request to: /api/alarm-templates/assignments?sensor_id=' + sensorId);
        const response = await fetch(`${API_BASE_URL}/alarm-templates/assignments?sensor_id=${sensorId}`, {
            headers: { 'Authorization': `Bearer ${auth.token}` }
        });

        if (!response.ok) {
            console.error('Response not OK:', response.status, response.statusText);
            throw new Error('Failed to load alarm assignments');
        }

        const data = await response.json();
        console.log('Alarm assignments response:', data);
        console.log('Response type:', Array.isArray(data) ? 'Array' : typeof data);
        // Backend returns array directly, not { assignments: [...] }
        const assignments = Array.isArray(data) ? data : (data.assignments || []);
        console.log('Assignments array:', assignments);
        console.log('Number of assignments:', assignments.length);

        const containerId = `sensor-alarm-assignments-${sensorId}`;
        console.log('Looking for container with ID:', containerId);
        const container = document.getElementById(containerId);
        console.log('Container found:', !!container);
        if (!container) {
            console.error('Container not found! Available containers:', 
                Array.from(document.querySelectorAll('[id^="sensor-alarm-assignments-"]')).map(el => el.id));
            return;
        }
        console.log('Updating container with', assignments.length, 'assignments');

        if (assignments.length === 0) {
            container.innerHTML = '<div class="text-sm text-gray-500 italic">No alarms assigned</div>';
            return;
        }

        container.innerHTML = assignments.map(assignment => {
            const isSuspended = assignment.is_suspended || false;
            const suspendedUntil = assignment.suspended_until ? new Date(assignment.suspended_until).toLocaleDateString() : null;
            
            return `
            <div class="flex items-center justify-between p-2 bg-gray-50 rounded-md ${isSuspended ? 'border-2 border-yellow-400' : ''}">
                <div class="flex-1">
                    <div class="flex items-center space-x-2">
                        <div class="text-sm font-medium text-gray-900">
                            ${assignment.custom_name || assignment.template_name}
                        </div>
                        ${isSuspended ? `
                            <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                Suspended until ${suspendedUntil}
                            </span>
                        ` : ''}
                    </div>
                    <div class="text-xs text-gray-500">
                        Check: ${assignment.check_time || 'N/A'} | Max: ${assignment.max_time_since_registration || 'N/A'} min
                    </div>
                </div>
                <div class="flex items-center space-x-2">
                    ${isSuspended ? `
                        <button type="button"
                                onclick="resumeAlarmAssignment('${assignment._id || assignment.id}', '${sensorId}')"
                                class="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded hover:bg-green-200"
                                title="Resume alarm now">
                            Resume
                        </button>
                    ` : `
                        <button type="button"
                                onclick="showSuspendAlarmModal('${assignment._id || assignment.id}', '${sensorId}')"
                                class="px-2 py-1 text-xs font-medium text-yellow-700 bg-yellow-100 rounded hover:bg-yellow-200"
                                title="Suspend alarm for holidays">
                            Suspend
                        </button>
                    `}
                    <label class="relative inline-flex items-center cursor-pointer" title="${assignment.enabled ? 'Enabled' : 'Disabled'}">
                        <input type="checkbox" ${assignment.enabled ? 'checked' : ''} 
                               onchange="toggleAlarmAssignment('${assignment._id || assignment.id}', this.checked, '${sensorId}')"
                               class="sr-only peer">
                        <div class="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                        <span class="ml-2 text-xs font-medium ${assignment.enabled ? 'text-green-700' : 'text-gray-500'}">${assignment.enabled ? 'ON' : 'OFF'}</span>
                    </label>
                    <button type="button"
                            onclick="removeAlarmAssignment('${assignment._id || assignment.id}', '${sensorId}')" 
                            class="text-red-600 hover:text-red-800"
                            title="Remove alarm">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </div>
        `;
        }).join('');

    } catch (error) {
        console.error('Error loading alarm assignments:', error);
        const container = document.getElementById(`sensor-alarm-assignments-${sensorId}`);
        if (container) {
            container.innerHTML = '<div class="text-sm text-red-600">Error loading alarms</div>';
        }
    }
}

/**
 * Show assign alarm modal
 */
export async function showAssignAlarmModal(sensorId, sensorType) {
    try {
        // Check if sensor already has an alarm
        const checkResponse = await fetch(`${API_BASE_URL}/alarm-templates/assignments?sensor_id=${sensorId}`, {
            headers: { 'Authorization': `Bearer ${auth.token}` }
        });
        
        let hasExistingAlarm = false;
        if (checkResponse.ok) {
            const existingAssignments = await checkResponse.json();
            const assignments = Array.isArray(existingAssignments) ? existingAssignments : (existingAssignments.assignments || []);
            hasExistingAlarm = assignments.length > 0;
        }
        
        // Load available templates
        const response = await fetch(`${API_BASE_URL}/alarm-templates/templates`, {
            headers: { 'Authorization': `Bearer ${auth.token}` }
        });

        if (!response.ok) {
            throw new Error('Failed to load alarm templates');
        }

        const data = await response.json();
        // Backend returns array directly, not { templates: [...] }
        let templates = Array.isArray(data) ? data : (data.templates || []);
        
        // Filter templates by sensor type
        if (sensorType) {
            templates = templates.filter(t => t.sensor_type === sensorType);
        }
        
        console.log('Loaded alarm templates for sensor type', sensorType, ':', templates);

        if (templates.length === 0) {
            showError(`No alarm templates available for ${sensorType || 'this sensor type'}. Please create a template first in Setup → Alarms.`);
            return;
        }
        
        // Store templates globally for preview function
        window.assignModalTemplates = templates;

        const modalTitle = hasExistingAlarm ? 'Change Alarm Template' : 'Assign Alarm Template';
        const modalButton = hasExistingAlarm ? 'Change Alarm' : 'Assign Alarm';
        const modalNote = hasExistingAlarm ? '<p class="text-xs text-yellow-700 bg-yellow-50 p-2 rounded">This sensor already has an alarm. Selecting a new template will replace the existing one.</p>' : '';

        const modalHtml = `
            <div class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-[60]">
                <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
                    <div class="px-6 py-4 border-b border-gray-200">
                        <h3 class="text-lg font-medium text-gray-900">${modalTitle}</h3>
                        ${sensorType ? `<p class="text-sm text-gray-500 mt-1">For ${sensorType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} sensors</p>` : ''}
                    </div>
                    <form onsubmit="handleAssignAlarm(event, '${sensorId}')" class="px-6 py-4">
                        ${modalNote}
                        <div class="space-y-4 ${modalNote ? 'mt-4' : ''}">
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">Select Template *</label>
                                <select id="assign-alarm-template" required onchange="showTemplatePreview()"
                                        class="block w-full px-3 py-2 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500">
                                    <option value="">Choose a template...</option>
                                    ${templates.map(t => {
                                        const scheduleCount = t.schedules?.length || 1;
                                        const scheduleText = scheduleCount > 1 ? ` (${scheduleCount} schedules)` : '';
                                        return `<option value="${t._id || t.id}">${t.template_name}${scheduleText}</option>`;
                                    }).join('')}
                                </select>
                            </div>
                            
                            <div id="template-preview" class="hidden bg-gray-50 rounded-lg p-3 text-sm">
                                <!-- Template details will be shown here -->
                            </div>
                            
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">Custom Name (Optional)</label>
                                <input type="text" id="assign-alarm-custom-name" 
                                       class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                                       placeholder="Override template name for this sensor">
                                <p class="mt-1 text-xs text-gray-500">Leave empty to use template name</p>
                            </div>
                        </div>
                        <div class="mt-6 flex justify-end space-x-3">
                            <button type="button" onclick="closeNestedNestedModal()" 
                                    class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                                Cancel
                            </button>
                            <button type="submit" 
                                    class="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700">
                                ${modalButton}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        `;

        // Use nested-nested modal for assign alarm (on top of sensor edit modal)
        let assignModal = document.getElementById('nested-nested-modal');
        if (!assignModal) {
            assignModal = document.createElement('div');
            assignModal.id = 'nested-nested-modal';
            assignModal.className = 'hidden';
            document.body.appendChild(assignModal);
        }
        
        assignModal.innerHTML = modalHtml;
        assignModal.classList.remove('hidden');

    } catch (error) {
        console.error('Error showing assign alarm modal:', error);
        showError('Failed to load alarm templates');
    }
}

/**
 * Show template preview when selecting from dropdown
 */
export function showTemplatePreview() {
    const select = document.getElementById('assign-alarm-template');
    const preview = document.getElementById('template-preview');
    
    if (!select || !preview) return;
    
    const templateId = select.value;
    if (!templateId || !window.assignModalTemplates) {
        preview.classList.add('hidden');
        return;
    }
    
    const template = window.assignModalTemplates.find(t => (t._id || t.id) === templateId);
    if (!template) {
        preview.classList.add('hidden');
        return;
    }
    
    const schedules = template.schedules || [];
    const schedulesHtml = schedules.map((schedule, index) => `
        <div class="${index > 0 ? 'mt-2 pt-2 border-t border-gray-200' : ''}">
            <div class="font-medium text-gray-700 text-xs mb-1">Schedule ${index + 1}</div>
            <div class="grid grid-cols-2 gap-2">
                <div>
                    <span class="text-gray-500">Check Time:</span>
                    <span class="ml-1 font-medium">${schedule.check_time}</span>
                </div>
                <div>
                    <span class="text-gray-500">Max:</span>
                    <span class="ml-1 font-medium">${schedule.max_time_since_registration} min</span>
                </div>
                <div class="col-span-2">
                    <span class="text-gray-500">Days:</span>
                    <span class="ml-1">${formatWeekdays(schedule.weekdays)}</span>
                </div>
            </div>
        </div>
    `).join('');
    
    preview.innerHTML = `
        <div class="text-xs font-medium text-gray-700 mb-2">Template Details:</div>
        ${schedulesHtml}
        ${template.actions?.send_sms ? `
            <div class="mt-2 pt-2 border-t border-gray-200 text-xs space-y-1">
                <div><span class="text-gray-500">SMS notifications enabled</span></div>
                ${template.actions.sms_message ? `
                    <div class="italic text-gray-600">"${template.actions.sms_message}"</div>
                ` : ''}
            </div>
        ` : ''}
    `;
    preview.classList.remove('hidden');
}

/**
 * Handle alarm assignment form submission
 */
export async function handleAssignAlarm(event, sensorId) {
    event.preventDefault();

    try {
        const templateId = document.getElementById('assign-alarm-template').value;
        const customName = document.getElementById('assign-alarm-custom-name').value;

        console.log('Assigning alarm:', { sensor_id: sensorId, template_id: templateId, custom_name: customName });

        const response = await fetch(`${API_BASE_URL}/alarm-templates/assignments`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${auth.token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                sensor_id: sensorId,
                template_id: templateId,
                custom_name: customName || undefined,
                enabled: true
            })
        });

        if (!response.ok) {
            const error = await response.json();
            console.error('Assignment error response:', error);
            
            // If it's a duplicate assignment error, show helpful message
            if (error.detail && error.detail.includes('already assigned')) {
                showError('This sensor already has this alarm template assigned. If you don\'t see it in the list above, it may have been assigned by another user or account. Please contact your administrator if you need to modify it.');
            } else {
                throw new Error(error.detail || 'Failed to assign alarm');
            }
            return;
        }

        closeNestedNestedModal();

        // Reload alarm assignments in the edit modal (without closing it)
        await loadSensorAlarmAssignments(sensorId);
        
        // If we came from location edit, also refresh the sensor list there
        const sourceLocationId = window.editSensorSourceLocation;
        if (sourceLocationId && window.loadAvailableSensorsForLocation) {
            await window.loadAvailableSensorsForLocation('edit-location-sensors-container', sourceLocationId);
        }

    } catch (error) {
        console.error('Error assigning alarm:', error);
        showError(error.message || 'Failed to assign alarm template');
    }
}

/**
 * Toggle alarm assignment enabled/disabled
 */
export async function toggleAlarmAssignment(assignmentId, enabled, sensorId) {
    try {
        const response = await fetch(`${API_BASE_URL}/alarm-templates/assignments/${assignmentId}`, {
            method: 'PATCH',
            headers: {
                'Authorization': `Bearer ${auth.token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ enabled })
        });

        if (!response.ok) {
            throw new Error('Failed to update alarm assignment');
        }

        showSuccess(enabled ? 'Alarm enabled' : 'Alarm disabled');
        
        // Reload assignments to update the UI (without closing the edit modal)
        if (sensorId) {
            await loadSensorAlarmAssignments(sensorId);
            
            // If we came from location edit, also refresh the sensor list there
            const sourceLocationId = window.editSensorSourceLocation;
            if (sourceLocationId && window.loadAvailableSensorsForLocation) {
                await window.loadAvailableSensorsForLocation('edit-location-sensors-container', sourceLocationId);
            }
        }

    } catch (error) {
        console.error('Error toggling alarm:', error);
        showError('Failed to update alarm status');
    }
}

/**
 * Remove alarm assignment
 */
export async function removeAlarmAssignment(assignmentId, sensorId) {
    try {
        const response = await fetch(`${API_BASE_URL}/alarm-templates/assignments/${assignmentId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${auth.token}` }
        });

        if (!response.ok) {
            throw new Error('Failed to remove alarm assignment');
        }

        // Reload alarm assignments to update the UI (modal stays open)
        await loadSensorAlarmAssignments(sensorId);
        
        // If we came from location edit, also refresh the sensor list there
        const sourceLocationId = window.editSensorSourceLocation;
        if (sourceLocationId && window.loadAvailableSensorsForLocation) {
            await window.loadAvailableSensorsForLocation('edit-location-sensors-container', sourceLocationId);
        }

        console.log('Alarm removed successfully. Modal remains open for user to continue editing or close.');

    } catch (error) {
        console.error('Error removing alarm:', error);
        showError('Failed to remove alarm assignment');
    }
}

/**
 * Show suspend alarm modal
 */
export async function showSuspendAlarmModal(assignmentId, sensorId) {
    const modalHtml = `
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-[60]">
            <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
                <div class="px-6 py-4 border-b border-gray-200">
                    <h3 class="text-lg font-medium text-gray-900">Suspend Alarm</h3>
                </div>
                <form onsubmit="handleSuspendAlarm(event, '${assignmentId}', '${sensorId}')" class="px-6 py-4 space-y-4">
                    <p class="text-sm text-gray-600">
                        Suspend this alarm for a specific number of days (useful for holidays, vacations, or maintenance periods).
                    </p>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-2">
                            Suspend for how many days?
                        </label>
                        <select id="suspend-days" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                            <option value="1">1 day</option>
                            <option value="2">2 days</option>
                            <option value="3">3 days</option>
                            <option value="7" selected>1 week (7 days)</option>
                            <option value="14">2 weeks (14 days)</option>
                            <option value="30">1 month (30 days)</option>
                            <option value="60">2 months (60 days)</option>
                            <option value="90">3 months (90 days)</option>
                        </select>
                    </div>
                    <div class="flex justify-end space-x-2 pt-4 border-t border-gray-200">
                        <button type="button" 
                                onclick="closeNestedNestedModal()"
                                class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                            Cancel
                        </button>
                        <button type="submit"
                                class="px-4 py-2 text-sm font-medium text-white bg-yellow-600 rounded-md hover:bg-yellow-700">
                            Suspend Alarm
                        </button>
                    </div>
                </form>
            </div>
        </div>
    `;

    // Use nested-nested modal
    let suspendModal = document.getElementById('nested-nested-modal');
    if (!suspendModal) {
        suspendModal = document.createElement('div');
        suspendModal.id = 'nested-nested-modal';
        suspendModal.className = 'hidden';
        document.body.appendChild(suspendModal);
    }
    
    suspendModal.innerHTML = modalHtml;
    suspendModal.classList.remove('hidden');
}

/**
 * Handle suspend alarm form submission
 */
export async function handleSuspendAlarm(event, assignmentId, sensorId) {
    event.preventDefault();
    const days = parseInt(document.getElementById('suspend-days').value);
    await suspendAlarmAssignment(assignmentId, sensorId, days);
}

/**
 * Suspend alarm assignment
 */
async function suspendAlarmAssignment(assignmentId, sensorId, days) {
    try {
        const response = await fetch(`${API_BASE_URL}/alarm-templates/assignments/${assignmentId}/suspend?days=${days}`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${auth.token}` }
        });

        if (!response.ok) {
            throw new Error('Failed to suspend alarm');
        }

        const data = await response.json();
        
        closeNestedNestedModal();
        
        // Reload alarm assignments (without closing edit modal)
        await loadSensorAlarmAssignments(sensorId);
        
        // If we came from location edit, also refresh the sensor list there
        const sourceLocationId = window.editSensorSourceLocation;
        if (sourceLocationId && window.loadAvailableSensorsForLocation) {
            await window.loadAvailableSensorsForLocation('edit-location-sensors-container', sourceLocationId);
        }

    } catch (error) {
        console.error('Error suspending alarm:', error);
        showError('Failed to suspend alarm');
    }
}

/**
 * Resume alarm assignment
 */
export async function resumeAlarmAssignment(assignmentId, sensorId) {
    try {
        const response = await fetch(`${API_BASE_URL}/alarm-templates/assignments/${assignmentId}/resume`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${auth.token}` }
        });

        if (!response.ok) {
            throw new Error('Failed to resume alarm');
        }

        // Reload alarm assignments (without closing edit modal)
        await loadSensorAlarmAssignments(sensorId);
        
        // If we came from location edit, also refresh the sensor list there
        const sourceLocationId = window.editSensorSourceLocation;
        if (sourceLocationId && window.loadAvailableSensorsForLocation) {
            await window.loadAvailableSensorsForLocation('edit-location-sensors-container', sourceLocationId);
        }

    } catch (error) {
        console.error('Error resuming alarm:', error);
        showError('Failed to resume alarm');
    }
}
